'use client';

import {
  type UIOption,
  mapInlineOption,
  normalizeExternalOptions,
} from '@/components/Form/Fields/shared/normalizeOptions';
import { type IDefaultFormBuilderProps } from '@/components/Form/Fields/types';
import Label from '@/components/Label/Label';
import { FormControl, FormHelperText, FormLabel } from '@/components/field';
import InfoIconModal from '@/components/infoIcon/InfoIconModal';
import LoadingBackdrop from '@/components/loadingBackdrop/LoadingBackdrop';
import { RichText } from '@/components/richText/RichText';
import { SwdFormControlLabel } from '@/components/swdFormControlLabel/SwdFormControlLabel';
import { SwdFormGroup } from '@/components/swdFormGroup/SwdFormGroup';
import { SwdRadio } from '@/components/swdRadio/SwdRadio';
import { SwdRadioGroup } from '@/components/swdRadioGroup/SwdRadioGroup';
import { hasContent, richtextToString } from '@/helper/RichText';
import type {
  DatasourceItemStoryblok,
  FormRadioGroupStoryblok,
  OptionItemStoryblok,
  RichtextStoryblok,
} from '@/types/component-types-sb';
import { type FieldInputProps, useFormikContext } from 'formik';
import { type FC, useEffect, useMemo, useState } from 'react';

interface IRadioButtonGroupField
  extends Omit<FormRadioGroupStoryblok, 'label'>, Omit<IDefaultFormBuilderProps, 'label'> {
  field: FieldInputProps<string>;
  label?: RichtextStoryblok | string;
}

const RadioButtonGroupField: FC<IRadioButtonGroupField> = (props) => {
  const [dynamicOptions, setDynamicOptions] = useState<UIOption[] | null>(null);

  const [isLoadingDynamicOptions, setIsLoadingDynamicOptions] = useState<boolean>(false);

  const { setFieldValue } = useFormikContext();
  const stateKey = props.field.name;

  const staticOptions: UIOption[] = useMemo(() => {
    const src = Array.isArray(props.options) ? props.options : [];
    return src
      .filter((opt): opt is OptionItemStoryblok => (opt as OptionItemStoryblok).component === 'OptionItem')
      .map(mapInlineOption);
  }, [props.options]);

  useEffect(() => {
    let cancelled = false;

    const expand = async () => {
      const src = Array.isArray(props.options) ? props.options : [];
      const hasDatasourceItems = src.some((opt) => (opt as DatasourceItemStoryblok).component === 'DatasourceItem');
      if (!hasDatasourceItems) {
        if (!cancelled) setDynamicOptions(null);
        return;
      }

      const results: UIOption[] = [];
      for (const item of src) {
        if ((item as OptionItemStoryblok).component === 'OptionItem') {
          results.push(mapInlineOption(item as OptionItemStoryblok));
          continue;
        }
        if ((item as DatasourceItemStoryblok).component === 'DatasourceItem') {
          const url = (item as DatasourceItemStoryblok).datasource?.trim();
          if (!url) continue;
          try {
            setIsLoadingDynamicOptions(true);
            const res = await fetch(url, { cache: 'no-store' });
            if (!res.ok) throw new Error(`Failed to load datasource ${url}: ${res.status}`);
            const json = (await res.json()) as unknown;
            const mapped = normalizeExternalOptions(json);
            if (mapped.length > 0) {
              results.push(...mapped);
            }
          } catch (_e) {
            // skip failed datasource
          } finally {
            setIsLoadingDynamicOptions(false);
          }
        }
      }

      if (!cancelled) setDynamicOptions(results);
    };

    void expand();
    return () => {
      cancelled = true;
    };
  }, [props.options]);

  const handleRadioChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = e.target.value;
    props.field.onChange(e);
    const opt = renderOptions.find((o) => String(o.value) === String(value));
    const label = typeof opt?.label === 'string' ? opt.label : richtextToString(opt?.label);
    setFieldValue(`${stateKey}__label`, label, false);
  };

  const renderOptions = dynamicOptions ?? staticOptions;

  return (
    <FormControl error={props.isInvalid}>
      {(props.label || !props.required || !!props.infoText) && (
        <FormLabel required={props.required}>
          {!!props.label && `${props.label} `}
          {hasContent(props.infoText) && (
            <>
              <InfoIconModal trackingLabel={richtextToString(props.label)}>
                <RichText text={props.infoText} />
              </InfoIconModal>{' '}
            </>
          )}
        </FormLabel>
      )}
      <LoadingBackdrop loading={isLoadingDynamicOptions} type="card" text="Daten werden geladen..." />
      {!isLoadingDynamicOptions && (
        <SwdFormGroup row={props.alignment === 'horizontal'}>
          <SwdRadioGroup {...props.field} id={props.id} disabled={props.disabled} onChange={handleRadioChange}>
            {renderOptions.map((option, index) => (
              <SwdFormControlLabel
                key={option._uid || option.value}
                control={
                  <SwdRadio data-testid={'form-builder-radio-item' + Number(index + 1)} value={String(option.value)} />
                }
                label={<Label text={option.label} />}
              />
            ))}
          </SwdRadioGroup>
        </SwdFormGroup>
      )}
      {props.isInvalid && props.errorMessage && <FormHelperText>{props.errorMessage}</FormHelperText>}
    </FormControl>
  );
};

export default RadioButtonGroupField;
